// filepath: apps/web/src/editors/bpm-designer/BpmEdgeGuardInput.tsx
//
// FR-UI-BLD-1 AC-3 - reusable guard formula input for edge properties.
//
// 250ms debounce from last keystroke. On validation success renders a
// green check + tooltip. On validation failure renders a red error
// icon + tooltip carrying the server's error message.
// INV-9: error message is rendered as a text node, never as HTML.

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetch } from "@/api";

interface BpmEdgeGuardInputProps {
  edgeId: string;
  value: string;
  onChange: (next: string) => void;
  ariaLabel?: string;
}

const DEBOUNCE_MS = 250;

interface FormulaValidateWire {
  ok: boolean;
  type?: string;
  error?: string;
}

export function BpmEdgeGuardInput({
  edgeId,
  value,
  onChange,
  ariaLabel,
}: BpmEdgeGuardInputProps): React.ReactElement {
  const { t } = useTranslation();
  const [debouncedValue, setDebouncedValue] = useState(value);
  const [status, setStatus] = useState<"idle" | "pending" | "ok" | "error">(
    "idle",
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [value]);

  useEffect(() => {
    if (debouncedValue.length === 0) {
      setStatus("idle");
      setErrorMessage(null);
      return;
    }
    let cancelled = false;
    setStatus("pending");
    void (async () => {
      try {
        const r = await apiFetch("/api/formula/validate", {
          method: "POST",
          init: {
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              formula: debouncedValue,
              context: "guard",
              field_id: edgeId,
            }),
          },
        });
        if (cancelled) return;
        if (!r.ok) {
          setStatus("error");
          setErrorMessage(`HTTP ${r.status}`);
          return;
        }
        const data = (await r.json()) as FormulaValidateWire;
        if (cancelled) return;
        if (data.ok) {
          setStatus("ok");
          setErrorMessage(null);
        } else {
          setStatus("error");
          setErrorMessage(data.error ?? "error");
        }
      } catch {
        if (cancelled) return;
        setStatus("error");
        setErrorMessage("network");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedValue, edgeId]);

  return (
    <div
      className="bpm-edge-guard-input"
      data-status={status}
      data-testid="bpm-edge-guard-input"
    >
      <input
        type="text"
        className="bpm-edge-guard-input__text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel ?? "Guard formula"}
        aria-invalid={status === "error"}
        data-testid="bpm-edge-guard-input-text"
      />
      <span
        className="bpm-edge-guard-input__status"
        aria-live="polite"
        data-testid="bpm-edge-guard-input-status"
      >
        {status === "ok" && (
          <span
            className="bpm-edge-guard-input__ok"
            title={t("admin.designer.guard.ok")}
          >
            ok
          </span>
        )}
        {status === "error" && (
          <span
            className="bpm-edge-guard-input__error"
            title={errorMessage ?? t("admin.designer.guard.error")}
          >
            {errorMessage ?? t("admin.designer.guard.error")}
          </span>
        )}
      </span>
    </div>
  );
}
